import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Download, Copy, Search, Loader2, Users, X } from "lucide-react";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchNonSubscribers, nonSubscribersCsvUrl } from "@/lib/api";
import { toast } from "sonner";

const SEGMENTS = [
  { id: "", label: "All", note: "everyone who hasn't paid" },
  { id: "never_paid", label: "Never paid", note: "signed up · never subscribed" },
  { id: "cancelled", label: "Cancelled", note: "was paying · now not" },
  { id: "paused", label: "Paused", note: "on the pause pathway" },
];

const SEGMENT_STYLE = {
  never_paid: "border-obsidian-600 text-bone-300",
  cancelled: "border-terracotta/60 text-terracotta bg-terracotta/10",
  paused: "border-bone-400/40 text-bone-300",
};

/**
 * AdminMarketing — the non-paid subscriber list for outbound marketing emails.
 * Search, segment, copy-all-emails, and CSV export to plug into any external tool.
 */
export default function AdminMarketing() {
  const [data, setData] = useState({ rows: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [segment, setSegment] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  // Debounce search so we're not thrashing Mongo on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchNonSubscribers({ q: debouncedQ, segment });
      setData(r);
    } catch (e) {
      console.error(e);
      toast.error("Could not load the marketing list.");
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, segment]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCopyEmails = async () => {
    const emails = (data.rows || []).map((r) => r.email).filter(Boolean).join(", ");
    if (!emails) {
      toast.info("No emails in the current view.");
      return;
    }
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`${data.rows.length} emails copied to clipboard.`);
    } catch (e) {
      console.error(e);
      toast.error("Could not copy the emails.");
    }
  };

  const handleDownloadCsv = () => {
    // Simple GET — server sends `Content-Disposition: attachment`.
    // Uses the session cookie automatically (withCredentials).
    const url = nonSubscribersCsvUrl(segment);
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const totalNonPaid = useMemo(
    () => (data.counts?.never_paid || 0) + (data.counts?.cancelled || 0) + (data.counts?.paused || 0),
    [data.counts]
  );

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
          data-testid="back-to-admin"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-10 sm:pt-16 pb-24">
        <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">
          Umbra · Marketing List
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight mb-4">
          Non-paid subscribers.
        </h1>
        <p className="font-serif italic text-base sm:text-lg text-bone-400 max-w-2xl leading-relaxed mb-10">
          Everyone with an Umbra account who is not currently on a paid plan. Search, segment by
          history, copy the whole list to your clipboard, or export a CSV to plug into any
          external tool.
        </p>

        {/* Summary + segment tabs */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="marketing-segments">
          <span
            data-testid="marketing-total"
            className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-terracotta border border-terracotta/40 px-3 py-2 min-h-[44px]"
          >
            <Users className="h-3 w-3" /> {totalNonPaid} total
          </span>
          {SEGMENTS.map((s) => {
            const count =
              s.id === ""
                ? totalNonPaid
                : data.counts?.[s.id] || 0;
            const active = segment === s.id;
            return (
              <button
                key={s.id || "all"}
                onClick={() => setSegment(s.id)}
                data-testid={`marketing-segment-${s.id || "all"}`}
                className={`font-sans uppercase tracking-[0.2em] text-[0.65rem] px-3 py-2 min-h-[44px] border transition-colors ${
                  active
                    ? "border-terracotta text-terracotta bg-terracotta/10"
                    : "border-obsidian-600 text-bone-400 hover:border-terracotta hover:text-terracotta"
                }`}
                title={s.note}
              >
                {s.label} <span className="opacity-70">· {count}</span>
              </button>
            );
          })}
        </div>

        {/* Search + actions */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-bone-500 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or name"
              data-testid="marketing-search"
              className="rounded-none bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-sans text-sm pl-10 pr-10 min-h-[44px]"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                data-testid="marketing-search-clear"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-bone-500 hover:text-terracotta p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            onClick={handleCopyEmails}
            data-testid="marketing-copy-btn"
            disabled={loading || (data.rows || []).length === 0}
            className="rounded-none border-obsidian-600 hover:border-terracotta bg-transparent text-bone-200 hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px] transition-colors"
          >
            <Copy className="h-4 w-4 mr-2" /> Copy emails
          </Button>
          <Button
            onClick={handleDownloadCsv}
            data-testid="marketing-csv-btn"
            disabled={totalNonPaid === 0}
            className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px] transition-colors"
          >
            <Download className="h-4 w-4 mr-2" /> Download CSV
          </Button>
        </div>

        {/* Result count for current view */}
        <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-4">
          Showing <span className="text-bone-200">{(data.rows || []).length}</span> of {data.total} matching
        </p>

        {/* Table */}
        {loading ? (
          <div className="flex items-center gap-3 p-8">
            <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
            <p className="font-sans text-bone-400">Gathering the list…</p>
          </div>
        ) : (data.rows || []).length === 0 ? (
          <div className="border border-obsidian-600 p-10 text-center">
            <p className="font-serif italic text-bone-300">No non-paid users match this view.</p>
          </div>
        ) : (
          <div className="border border-obsidian-600 overflow-x-auto" data-testid="marketing-table">
            <table className="w-full min-w-[720px] text-left font-sans text-sm">
              <thead>
                <tr className="border-b border-obsidian-600 bg-obsidian-800/50">
                  <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Email</th>
                  <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Name</th>
                  <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Joined</th>
                  <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Founder</th>
                  <th className="p-4 font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-500">Segment</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr
                    key={r.user_id}
                    data-testid={`marketing-row-${r.user_id}`}
                    className="border-b border-obsidian-600 last:border-0 hover:bg-obsidian-800/40 transition-colors"
                  >
                    <td className="p-4 text-bone-200 font-mono text-xs sm:text-sm break-all">
                      {r.email || <span className="text-bone-500 italic">—</span>}
                    </td>
                    <td className="p-4 text-bone-300">
                      {r.name || <span className="text-bone-500 italic">—</span>}
                    </td>
                    <td className="p-4 text-bone-400 whitespace-nowrap">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : <span className="text-bone-500 italic">—</span>}
                    </td>
                    <td className="p-4 text-bone-400 whitespace-nowrap">
                      {r.founder_number ? `#${r.founder_number}` : <span className="text-bone-500">—</span>}
                    </td>
                    <td className="p-4">
                      <span
                        data-testid={`marketing-segment-badge-${r.user_id}`}
                        className={`inline-flex items-center font-sans uppercase tracking-[0.2em] text-[0.55rem] px-2 py-1 border ${SEGMENT_STYLE[r.segment] || SEGMENT_STYLE.never_paid}`}
                      >
                        {r.segment.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.total > (data.rows || []).length && (
          <p className="mt-4 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
            Showing first {data.rows.length}. Refine your search or download the CSV for the full set.
          </p>
        )}
      </div>
    </div>
  );
}
